import { nextDelayMs } from './backoff'
import { classifyOutcome } from './classify'
import type { QueueStorage } from './storage'
import type { ItemRefusal, QueuedTransaction, RejectedRow, SyncTransport } from './types'

export type FlushEngineOptions = {
  storage: QueueStorage
  transport: SyncTransport
  /** Called once per flush when the server says the session expired. */
  refreshSession?: () => Promise<boolean>
  /** Largest number of transactions in one `/sync` call. */
  batchSize?: number
  /** Injectable clock/jitter — the tests pin both. */
  now?: () => number
  random?: () => number
  onError?: (message: string, error: unknown) => void
}

export type FlushResult = {
  /** Another flush was already running, so this call did nothing. */
  skipped: boolean
  /** Transactions the server accepted this run. */
  sent: number
  /** Still queued afterwards (failed transiently, or not yet due). */
  pending: number
  /** Waiting on a human. */
  needsAttention: number
  /** The session expired and could not be refreshed — work is kept, not lost. */
  needsAuth: boolean
}

export type DrainResult = {
  /** Nothing is waiting to send any more. */
  drained: boolean
  pending: number
  needsAttention: number
}

export type FlushEngine = {
  flush: (options?: { ignoreBackoff?: boolean }) => Promise<FlushResult>
  /** Empty the queue — used at shift change, which requires a live connection. */
  drain: (options?: { maxRounds?: number }) => Promise<DrainResult>
  counts: () => Promise<{ pending: number, needsAttention: number, synced: number }>
  /** Requeue a refused row after its cause has been dealt with. */
  retry: (id: string) => Promise<void>
  /** Remove a row permanently. */
  discard: (id: string) => Promise<void>
}

/**
 * Drains the queue into `/sync` calls.
 *
 * Invariants worth stating, because an operator's work depends on them:
 *  - **Single-flight.** Two overlapping flushes would send the same rows twice
 *    and race on their state; the second call returns immediately instead.
 *  - **Nothing is dropped on failure.** Only the server accepting a row, or
 *    explicitly refusing it, moves it out of `pending`.
 *  - **FIFO per type**, oldest transaction first, so dependent work stays ordered.
 *  - **Replay is safe** because the row carries the client-minted id the
 *    `/sync` endpoint upserts on — a lost response costs a duplicate send, not
 *    a duplicate record.
 */
export function createFlushEngine(options: FlushEngineOptions): FlushEngine {
  const {
    storage,
    transport,
    refreshSession,
    batchSize = 50,
    now = () => Date.now(),
    random = Math.random,
    onError,
  } = options

  let running = false

  async function counts() {
    const rows = await storage.all()
    return {
      pending: rows.filter(r => r.state === 'pending').length,
      needsAttention: rows.filter(r => r.state === 'needs-attention').length,
      synced: rows.filter(r => r.state === 'synced').length,
    }
  }

  async function markSynced(transaction: QueuedTransaction) {
    await storage.put({ ...transaction, state: 'synced', syncedAt: now(), lastError: undefined, refusals: undefined })
  }

  async function markNeedsAttention(transaction: QueuedTransaction, reason: string, refusals?: ItemRefusal[]) {
    await storage.put({ ...transaction, state: 'needs-attention', lastError: reason, refusals })
  }

  async function scheduleRetry(transaction: QueuedTransaction, reason: string) {
    const attempts = transaction.attempts + 1
    await storage.put({
      ...transaction,
      state: 'pending',
      attempts,
      nextAttemptAt: now() + nextDelayMs(attempts, { random }),
      lastError: reason,
    })
  }

  type Refusal = { reason: string, refusals?: ItemRefusal[] }

  /** Rejected rows are matched by the id we minted; fall back to batch position. */
  function rejectedIds(batch: QueuedTransaction[], rejected: RejectedRow[]): Map<string, Refusal> {
    const byId = new Map<string, Refusal>()
    for (const row of rejected) {
      const reason = row.reason?.trim() || 'Rejected by the server'
      const byExternalId = row.externalId
        ? batch.find(op => op.id === row.externalId)
        : undefined
      const byIndex = typeof row.rowIndex === 'number' ? batch[row.rowIndex] : undefined
      const target = byExternalId ?? byIndex
      if (target) byId.set(target.id, { reason, refusals: row.refusals ?? undefined })
    }
    return byId
  }

  async function sendBatch(
    type: string,
    batch: QueuedTransaction[],
    allowRefresh: boolean,
  ): Promise<{ sent: number, needsAuth: boolean, stillPending: boolean }> {
    let outcome
    try {
      outcome = await transport(type, batch)
    } catch (error) {
      // A transport that throws is indistinguishable from being offline —
      // treat it as transient rather than losing the work.
      outcome = { status: null, message: error instanceof Error ? error.message : 'Send failed' }
      onError?.('Sync transport threw', error)
    }

    const disposition = classifyOutcome(outcome)

    switch (disposition.kind) {
      case 'accepted': {
        for (const transaction of batch) await markSynced(transaction)
        return { sent: batch.length, needsAuth: false, stillPending: false }
      }

      case 'partial': {
        const refusals = rejectedIds(batch, disposition.rejected)
        let sent = 0
        for (const transaction of batch) {
          const refusal = refusals.get(transaction.id)
          if (refusal) {
            await markNeedsAttention(transaction, refusal.reason, refusal.refusals)
          } else {
            await markSynced(transaction)
            sent += 1
          }
        }
        return { sent, needsAuth: false, stillPending: false }
      }

      case 'auth': {
        // Refresh once per flush, then replay the batch — the same ids upsert,
        // so a retry after re-authenticating cannot duplicate anything.
        if (allowRefresh && refreshSession && (await refreshSession())) {
          return sendBatch(type, batch, false)
        }
        for (const transaction of batch) {
          await scheduleRetry(transaction, disposition.reason)
        }
        return { sent: 0, needsAuth: true, stillPending: true }
      }

      case 'retry': {
        for (const transaction of batch) await scheduleRetry(transaction, disposition.reason)
        return { sent: 0, needsAuth: false, stillPending: true }
      }

      case 'permanent': {
        // Refused for good: it is out of the queue, so nothing behind it is
        // waiting on it any more.
        for (const transaction of batch) await markNeedsAttention(transaction, disposition.reason)
        return { sent: 0, needsAuth: false, stillPending: false }
      }
    }
  }

  async function flush(options: { ignoreBackoff?: boolean } = {}): Promise<FlushResult> {
    if (running) {
      const { pending, needsAttention } = await counts()
      return { skipped: true, sent: 0, pending, needsAttention, needsAuth: false }
    }

    running = true
    let sent = 0
    let needsAuth = false

    try {
      // A shift change must not sit out a retry timer, so drain() asks for
      // everything pending rather than only what is due.
      const due = options.ignoreBackoff
        ? (await storage.all()).filter(op => op.state === 'pending')
        : await storage.due(now())

      // Batched into RUNS of the same type, in the order the operator acted.
      // Types never share a request — each maps to its own endpoint — but they
      // must not overtake each other either: finishing an order and loading the
      // next order on the same truck are different types, and the server refuses
      // the second until the first has landed. Grouping every transaction of a
      // type into one batch would send a later load ahead of an earlier
      // completion, and the operator's work would come back refused.
      //
      // A run also never mixes log-ins: work recorded under a log-in that has
      // since run out is sent with that log-in beside the live one, and a
      // request can carry only one.
      const runs: Array<{ type: string, batch: QueuedTransaction[] }> = []
      for (const transaction of due) {
        const run = runs[runs.length - 1]
        if (run && run.type === transaction.type && run.batch.length < batchSize
          && (run.batch[0]!.recordedSession ?? null) === (transaction.recordedSession ?? null)) {
          run.batch.push(transaction)
        } else {
          runs.push({ type: transaction.type, batch: [transaction] })
        }
      }

      for (const { type, batch } of runs) {
        const result = await sendBatch(type, batch, true)
        sent += result.sent
        if (result.needsAuth) {
          needsAuth = true
          break // no point sending the rest of the queue signed out
        }
        if (result.stillPending) {
          // Whatever comes after this may depend on it — the pallets that go
          // on an order before it is completed, the completion before the next
          // order loads. Leave the rest for the next flush rather than sending
          // work whose prerequisite is still sitting in the queue.
          break
        }
      }
    } finally {
      running = false
    }

    const { pending, needsAttention } = await counts()
    return { skipped: false, sent, pending, needsAttention, needsAuth }
  }

  /**
   * Push until nothing is left pending.
   *
   * This is what makes the session-boundary rule real: ending a shift requires
   * a live connection precisely so the queue can be emptied first, which is why
   * queued work never outlives the session that created it — and therefore why
   * the server's request-time actor stamp is always the right person. Refused
   * rows count as drained: they are no longer waiting to send, though a human
   * still has to deal with them.
   */
  async function drain(options: { maxRounds?: number } = {}): Promise<DrainResult> {
    const maxRounds = options.maxRounds ?? 5
    let last = await counts()

    for (let round = 0; round < maxRounds; round += 1) {
      if (last.pending === 0) break
      const result = await flush({ ignoreBackoff: true })
      const next = await counts()
      // No progress this round means the network or the server is refusing to
      // cooperate; report honestly instead of spinning.
      if (result.needsAuth || next.pending === last.pending) {
        last = next
        break
      }
      last = next
    }

    return { drained: last.pending === 0, pending: last.pending, needsAttention: last.needsAttention }
  }

  /**
   * Put a refused row back in the queue with a clean slate.
   *
   * Used from the needs-attention screen once the cause is dealt with (the
   * location exists now, the mapping was fixed). Attempts and the backoff
   * window reset so it goes out on the next flush rather than serving an old
   * penalty. Rows the server already accepted are untouchable — re-sending
   * accepted work is exactly what the external-key design exists to prevent.
   */
  async function retry(id: string): Promise<void> {
    const row = (await storage.all()).find(op => op.id === id)
    if (!row || row.state === 'synced') return
    await storage.put({
      ...row,
      state: 'pending',
      attempts: 0,
      nextAttemptAt: 0,
      lastError: undefined,
      refusals: undefined,
    })
  }

  /** Drop a row for good — the operator/supervisor decided it should not be sent. */
  async function discard(id: string): Promise<void> {
    await storage.remove(id)
  }

  return { flush, drain, counts, retry, discard }
}
